import { useState } from 'react';
import { createQueryDataTable, type PropsOfQueryDataTable, type RowOfQueryDataTable } from '~/shared/data-tables/query/QueryDataTable';
import { Button } from '~/shared/components/buttons/Button';
import { formatToShortDateOrNull, formatToDateTwoYearAndTimeOrNull } from '~/util/date';
import { RenderPatientNameNameCell } from '~/features/intake/patients/PatientNameColumn';
import { RenderAddressCell } from '~/shared/data-tables/column-types/AddressColumn';
import { getLegacyReferralOptions, LegacyReferralStatusEnum, makeLegacyReferralDecisionOptions } from '@generated';
import { useMutation } from '~/util/http/useMutation';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { Check, CheckIcon, EyeIcon, NotebookIcon, XIcon } from 'lucide-react';
import { Modal } from '~/shared/components/modal/Modal';
import { UpdateLegacyReferralModal } from './UpdateLegacyReferralModal';
import { useNavigate } from 'react-router';
import { TableActions } from '~/shared/data-tables/TableActions';
import { getDisciplineAbbreviations } from '../referralHelpers';

const LegacyReferralReportDataTable = createQueryDataTable('legacy-referral');
type RowType = RowOfQueryDataTable<typeof LegacyReferralReportDataTable>;
type TableProps = PropsOfQueryDataTable<typeof LegacyReferralReportDataTable>;

function ActionsCell({ row }: { row: RowType }) {
	const navigate = useNavigate();
	const makeDecision = useMutation({
		...makeLegacyReferralDecisionOptions,
		invalidateQueryKeys: [getLegacyReferralOptions(row.id).queryKey, getSearchQueryKey('legacy-referral')]
	});
	const [showNotesModal, setShowNotesModal] = useState(false);
	const [showPrompt, setShowPrompt] = useState(false);

	const navigateToReferral = () => {
		navigate(`${row.id}`);
	};

	if (row.statusId === LegacyReferralStatusEnum.Ntfc) {
		return (
			<TableActions>
				<TableActions.ViewButton aria-label="View" onClick={() => navigateToReferral()} />
				{showNotesModal && <UpdateLegacyReferralModal referral={row} closeModal={() => setShowNotesModal(false)} />}
				<Button Icon={NotebookIcon} aria-label="Notes" onClick={() => setShowNotesModal(true)} />
			</TableActions>
		);
	}

	const handleDecision = (accepted: boolean) => {
		makeDecision.mutate(
			{
				referralId: row.id,
				accepted
			},
			{
				onSettled: () => {
					setShowPrompt(false);
				}
			}
		);
	};

	return (
		<TableActions>
			<Button Icon={EyeIcon} aria-label="View" onClick={() => navigateToReferral()} />
			<Button Icon={Check} aria-label="Accept/Reject" onClick={() => setShowPrompt(true)} />
			<Button Icon={NotebookIcon} aria-label="Notes" onClick={() => setShowNotesModal(true)} />
			{showNotesModal && <UpdateLegacyReferralModal referral={row} closeModal={() => setShowNotesModal(false)} />}
			{showPrompt && (
				<Modal title="Accept/Reject" closeModal={() => setShowPrompt(false)}>
					<p className="text-gray-700">Do you want to accept this referral?</p>
					<p className="mt-2 text-sm text-gray-500">
						Note: Accepting will allow you to staff this referral. Rejecting will remove this referral from your queue and prevent you
						from staffing it.
					</p>
					<div className="mt-6 flex justify-end gap-2">
						<Button
							intent="destructive"
							autoFocus
							onClick={() => handleDecision(false)}
							label="Reject"
							Icon={XIcon}
							isPending={makeDecision.isPending}
						/>
						<Button autoFocus onClick={() => handleDecision(true)} label="Accept" Icon={CheckIcon} isPending={makeDecision.isPending} />
					</div>
				</Modal>
			)}
		</TableActions>
	);
}

const columnDefinitions = LegacyReferralReportDataTable.columns([
	{
		header: 'Patient',
		key: 'patientLastName',
		sortable: true,
		renderCell: row => (
			<RenderPatientNameNameCell
				patient={{ patientIdentifier: row.mrNumber, patientName: `${row.patientLastName}, ${row.patientFirstName}` }}
			/>
		)
	},
	{
		header: 'Address',
		key: 'address',
		width: 'w-[200px]',
		renderCell: row => <RenderAddressCell address={row.address} />
	},
	{
		header: 'SOC',
		key: 'expectedStartOfCareDate',
		sortable: true,
		width: 'w-[120px]',
		renderCell: row => formatToShortDateOrNull(row.expectedStartOfCareDate, '-')
	},
	{
		header: 'Diagnosis',
		key: 'primaryDiagnoses',
		sortable: true,
		cellClassName: 'item-center',
		renderCell: row => (row.primaryDiagnoses ? row.primaryDiagnoses : null)
	},
	{
		header: 'Status',
		key: 'statusId',
		sortable: true,
		renderCell: row => row.statusLabel
	},
	{
		header: 'Area',
		key: 'areaId',
		sortable: true,
		renderCell: row => row.areaLabel
	},
	{
		header: 'Staffed',
		key: 'fullyStaffed',
		sortable: true,
		width: 'w-[80px]',
		renderCell: row => (row.fullyStaffed ? 'Yes' : 'No')
	},
	{
		header: 'Needed',
		key: 'disciplinesNeeded',
		renderCell: row => getDisciplineAbbreviations(row.disciplinesNeeded).join(', ')
	},
	{
		header: 'Remaining',
		key: 'disciplinesNeedStaffing',
		renderCell: row => getDisciplineAbbreviations(row.disciplinesNeedStaffing).join(', ')
	},
	{
		header: 'Resp / Notified',
		key: 'totalResponded',
		width: 'w-[100px]',
		renderCell: row => `${row.totalRespondedYes ?? 0} yes / ${row.totalResponded ?? 0} / ${row.totalNotifications ?? 0}`
	},
	{
		header: 'Created On',
		key: 'dateCreated',
		sortable: true,
		filterNames: ['dateCreated'],
		renderCell: row => formatToDateTwoYearAndTimeOrNull(row.dateCreated, '-')
	},
	{
		header: 'Actions',
		key: 'actions',
		width: 'w-[160px]',
		renderCell: row => <ActionsCell row={row} />
	}
]);

export function LegacyReferralsTable(props: TableProps) {
	return <LegacyReferralReportDataTable.Table columns={columnDefinitions} emptyMessage="No referrals" {...props} />;
}
